import React from "react";
import { Money } from "@/helpers/Money";
import { MarketingIcon } from "@/components/icons";
import { variantSelect } from "@/store";

// Types for clarity
export type PromoEntry = {
  collection: string;
  discountType: string;
  discountValue: number;
  discountCode: string;
};

export type Collection = { id: string; handle: string; title: string };

export function getFinalAtcCtaPrice({
  productCollections,
  basePriceCents,
  sellingPlan,
  variant,
  product,
  currency = "$",
  strikeThroughSize,
  priceSize,
  strikeThroughColor,
  priceColor,
  bannerPadding,
}: {
  productCollections: Collection[] | undefined;
  basePriceCents: number | undefined;
  sellingPlan: any;
  variant: any;
  product?: any;
  currency?: "$" | "¢";
  strikeThroughSize?: string;
  priceSize?: string;
  strikeThroughColor?: string;
  priceColor?: string;
  bannerPadding?: string;
}): {
  display: React.ReactNode;
  discounted: string | null;
  banner: React.ReactNode | null;
  currentPrice: React.ReactNode;
  /**
   * The reference price (in cents) that `currentPrice` displays — i.e. the
   * pre-promo line price. Exposed so dependent UI (e.g. per-pod pricing) can
   * stay in sync with the headline instead of recomputing a divergent base.
   */
  currentPriceCents: number;
  /**
   * The discounted (post-promo) price in cents when a promo applies, else null.
   * Mirrors `discounted`, in cents, for dependent UI (e.g. per-pod pricing).
   */
  discountedCents: number | null;
} {
  // The discounted PRICE is collection-based: it shows for everyone browsing a
  // product in the promo's collection (advertises the running promo), whether
  // or not a code is in the URL. Multiplier / quantity-discount variants are
  // intentionally NOT excluded.
  const promoEntries: PromoEntry[] = (window as any).promoDiscountEntries || [];
  const matchingPromo = promoEntries
    .filter((entry) =>
      productCollections?.some(
        (col) => col.handle === entry.collection || col.id === entry.collection
      )
    )
    .sort((a, b) => b.discountValue - a.discountValue)[0];

  const isPromoDiscountEligible = matchingPromo || false;

  const subscriptionPrice = variantSelect.lowestPrice(variant, product);
  const onetimePrice = variantSelect.onetimePrice(variant, product);

  // If no promo, return the normal price (this still reflects the automatic
  // box/quantity discount via lowestPrice, which matches the cart when no
  // code is applied).
  if (!isPromoDiscountEligible) {
    return {
      display: sellingPlan ? subscriptionPrice.format() : onetimePrice.format(),
      discounted: null,
      banner: null,
      currentPrice: sellingPlan
        ? subscriptionPrice.format()
        : onetimePrice.format(),
      currentPriceCents: sellingPlan
        ? subscriptionPrice.asCents()
        : onetimePrice.asCents(),
      discountedCents: null,
    };
  }

  // Calculate discounted price
  const {
    discountType,
    discountValue,
    discountCode = "",
  } = isPromoDiscountEligible;
  const type = discountType?.toLowerCase();
  const discountPercent = Number(discountValue ?? 0);

  // Resolve how this variant expands into cart line items. Multiplier variants
  // are added to the cart as the base variant (multiplier=1) times a quantity,
  // so the promo must be computed off that same base line.
  const { variantToAdd: resolvedBaseVariant, quantity: resolvedQuantity } =
    product
      ? variantSelect.resolveVariantAndQuantityForCartAdd(
          variant,
          product.variants
        )
      : { variantToAdd: variant, quantity: 1 };

  // The line price the promo is applied on top of: base-variant unit price with
  // the subscription discount applied but WITHOUT the box/quantity discount
  // (the promo code replaces it). This equals the cart's original_line_price.
  const promoBaseUnitCents = (
    sellingPlan
      ? variantSelect.lowestPrice(resolvedBaseVariant, product)
      : variantSelect.onetimePrice(resolvedBaseVariant, product)
  ).asCents();
  const promoBaseLineCents = promoBaseUnitCents * resolvedQuantity;

  // Stack the box/quantity discount on top of the subscription price (already
  // baked into promoBaseUnitCents), before the promo code — mirroring the cart:
  // "buy more save more → subscription → code". This is the REAL cart price, so
  // it applies to the promo display everywhere — the cart's discount stacking
  // is global, not per-bucket.
  // NOTE: predicated on Recharge being configured to actually stack these; ship
  // in lockstep with that config, and reconcile the box-step rounding with a
  // real stacked cart once it's live.
  // Round the box step UP per unit, matching `lowestPrice`/`onetimePrice`
  // (Money.fromCentsRoundedUp) and the PLP card, so PDP and PLP prices agree.
  const boxPercent = variantSelect.quantityDiscountPercent(variant);
  const stackedUnitCents =
    boxPercent > 0
      ? Math.ceil(promoBaseUnitCents * (1 - boxPercent / 100))
      : promoBaseUnitCents;
  const stackedLineCents = stackedUnitCents * resolvedQuantity;

  let finalPrice = stackedLineCents;

  if (type === "percentage") {
    // Mirror Shopify's per-unit ("each") code allocation: floor the discount
    // per unit, then multiply by quantity, so the button matches the cart.
    const perUnitDiscount = Math.floor(
      stackedUnitCents * (discountPercent / 100)
    );
    finalPrice = Math.max(
      0,
      stackedLineCents - perUnitDiscount * resolvedQuantity
    );
  } else if (type === "fixed") {
    finalPrice = Math.max(
      0,
      stackedLineCents - Math.ceil(discountPercent * 100)
    );
  }

  const formattedDiscounted = Money.from(finalPrice / 100, currency).format();

  // Reference price shown as `currentPrice` (the pre-promo line price).
  const referenceCents = promoBaseLineCents;
  const originalPrice = Money.from(referenceCents / 100, currency).format();

  // Strikethrough shown in the ATC/sticky button. Match the PDP "List price"
  // label — compare-at if it's above the price, otherwise the one-time price —
  // falling back to the pre-promo line price so it never strikes below the
  // discounted price.
  const compareAtCents = variant?.compareAtPrice?.asCents?.();
  const priceCents = variant?.price?.asCents?.();
  const listReferenceCents =
    compareAtCents != null && priceCents != null && compareAtCents > priceCents
      ? compareAtCents
      : onetimePrice.asCents();
  const strikeThroughCents =
    listReferenceCents > finalPrice ? listReferenceCents : promoBaseLineCents;
  const strikeThroughPrice = Money.from(
    strikeThroughCents / 100,
    currency
  ).format();

  // Banner JSX if eligible
  let banner: React.ReactNode = null;
  if (discountValue && discountCode) {
    const discountText =
      type === "fixed" ? `$${discountValue} OFF` : `${discountValue}% OFF`;

    banner = (
      <div
        className={`mt-4 flex items-center justify-center rounded-[3px] border border-solid border-secondary-500 bg-secondary-100 ${
          bannerPadding || "px-4 py-[10px]"
        }`}
      >
        <MarketingIcon />
        <p className="ml-[10px] text-left text-xs">
          You`re getting <strong>{discountText}</strong> with code{" "}
          <strong>{discountCode}</strong> auto-applied
        </p>
      </div>
    );
  }

  // Return display as React.createElement (for .ts file compatibility)
  return {
    display: (
      <>
        <span
          className={`mr-2 font-normal line-through ${strikeThroughSize} ${strikeThroughColor} group-hover:text-white`}
        >
          {strikeThroughPrice}
        </span>
        <span className={`${priceSize} ${priceColor} group-hover:text-white`}>
          {formattedDiscounted}
        </span>
      </>
    ),
    discounted: formattedDiscounted,
    banner,
    currentPrice: originalPrice,
    currentPriceCents: referenceCents,
    discountedCents: finalPrice,
  };
}
